import React, { useState, useMemo } from 'react'

import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { useTranslation } from 'react-i18next'

import { CreditCardIcon } from '@/assets/images/icons/credit-card'
import { Button } from '@/shared/components/atoms/Button'
import { Checkbox } from '@/shared/components/atoms/Checkbox'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import { Skeleton } from '@/shared/components/atoms/Skeleton'

interface StripeCardFormProps {
	onSubmit: (paymentIntentId: string, paymentMethodId: string) => Promise<void>
	onCancel: () => void
	isSubmitting: boolean
	availablePaymentMethods?: any
	isLoadingPaymentMethods?: boolean
	onSelectPaymentMethod: (
		paymentPlatformMethodId: number,
		type: 'CARD' | 'SEPA'
	) => Promise<void>
	isCreatingIntent?: boolean
	hasClientSecret?: boolean
	selectedPaymentMethodType?: 'CARD' | 'SEPA' | null
	clientSecretValue?: string | null
}

type PaymentMethodType = 'CARD' | 'SEPA'

// Selection form component (doesn't use Stripe hooks)
const PaymentMethodSelection: React.FC<{
	availablePaymentMethods?: any
	isLoadingPaymentMethods?: boolean
	onSelectPaymentMethod: (
		paymentPlatformMethodId: number,
		type: 'CARD' | 'SEPA'
	) => Promise<void>
	isCreatingIntent?: boolean
	onCancel: () => void
	error?: string | null
}> = ({
	availablePaymentMethods,
	isLoadingPaymentMethods,
	onSelectPaymentMethod,
	isCreatingIntent,
	onCancel,
	error,
}) => {
	const { t } = useTranslation('common')
	const [localError, setLocalError] = useState<string | null>(error || null)

	// Extract available payment methods (CARD and/or SEPA)
	const availableMethods = useMemo(() => {
		if (!availablePaymentMethods) return { card: null, sepa: null }

		const methods =
			availablePaymentMethods?.data || availablePaymentMethods || []
		const methodsArray = Array.isArray(methods) ? methods : []

		const cardMethod = methodsArray.find((method: any) => {
			const methodData = method?.data || method
			return methodData?.payment_type === 'CARD'
		})

		const sepaMethod = methodsArray.find((method: any) => {
			const methodData = method?.data || method
			return methodData?.payment_type === 'SEPA'
		})

		return {
			card: cardMethod
				? {
						id: cardMethod.id || cardMethod.payment_platform_method_id,
						name: cardMethod.name || 'Credit Card',
					}
				: null,
			sepa: sepaMethod
				? {
						id: sepaMethod.id || sepaMethod.payment_platform_method_id,
						name: sepaMethod.name || 'SEPA',
					}
				: null,
		}
	}, [availablePaymentMethods])

	const handleSelectPaymentMethod = async (type: PaymentMethodType) => {
		try {
			setLocalError(null)
			const method =
				type === 'CARD' ? availableMethods.card : availableMethods.sepa
			if (!method) {
				setLocalError('Payment method not available')
				return
			}

			await onSelectPaymentMethod(method.id, type)
		} catch (err: any) {
			setLocalError(err?.message || 'Failed to initialize payment method')
		}
	}

	return (
		<div className="space-y-6">
			{(error || localError) && (
				<div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
					<p className="text-sm text-destructive">{error || localError}</p>
				</div>
			)}

			{isLoadingPaymentMethods ? (
				<div className="space-y-4 min-h-[550px]">
					<Skeleton className="h-4 w-64" />
					<div className="flex gap-4">
						<Skeleton className="w-[200px] h-[82px] rounded-lg" />
						<Skeleton className="w-[200px] h-[82px] rounded-lg" />
					</div>
				</div>
			) : isCreatingIntent ? (
				<div className="space-y-4 min-h-[550px]">
					<Skeleton className="h-4 w-64" />
					<div className="flex gap-4">
						<Skeleton className="w-[200px] h-[82px] rounded-lg" />
						<Skeleton className="w-[200px] h-[82px] rounded-lg" />
					</div>
				</div>
			) : (
				<div className="space-y-4">
					<p className="text-sm text-muted-foreground">
						Select a payment method to continue:
					</p>

					<div className="flex gap-4">
						{availableMethods.card && (
							<Button
								type="button"
								variant="outline"
								onClick={() => handleSelectPaymentMethod('CARD')}
								disabled={isCreatingIntent}
								className={`flex flex-col items-start justify-center w-[200px] h-[82px] rounded-lg border transition-colors ${
									isCreatingIntent
										? 'opacity-50 cursor-not-allowed'
										: 'hover:bg-muted dark:hover:bg-card'
								}`}
							>
								<div className="text-foreground">
									<CreditCardIcon color="currentColor" width={24} height={24} />
								</div>
								<span className="font-medium text-base">Credit Card</span>
							</Button>
						)}

						{availableMethods.sepa && (
							<Button
								type="button"
								variant="outline"
								onClick={() => handleSelectPaymentMethod('SEPA')}
								disabled={isCreatingIntent}
								className={`flex flex-col items-start justify-center w-[200px] h-[82px] rounded-lg border transition-colors ${
									isCreatingIntent
										? 'opacity-50 cursor-not-allowed'
										: 'hover:bg-muted dark:hover:bg-card'
								}`}
							>
								<div className="text-foreground">
									<CreditCardIcon color="currentColor" width={24} height={24} />
								</div>
								<span className="font-medium text-base">SEPA</span>
							</Button>
						)}
					</div>

					{!availableMethods.card && !availableMethods.sepa && (
						<div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
							<p className="text-sm text-destructive">
								No payment methods available. Please contact support.
							</p>
						</div>
					)}
				</div>
			)}

			<div className="flex gap-4 pt-6">
				<Button
					type="button"
					variant="outline"
					onClick={onCancel}
					className="rounded-full cursor-pointer"
					disabled={isCreatingIntent}
				>
					{t('actions.cancel')}
				</Button>
			</div>
		</div>
	)
}

// SEPA form component (custom form for SEPA direct debit)
const SepaPaymentForm: React.FC<{
	onSubmit: (paymentIntentId: string, paymentMethodId: string) => Promise<void>
	onCancel: () => void
	isSubmitting: boolean
	clientSecret: string
	stripe: any
}> = ({ onSubmit, onCancel, isSubmitting, clientSecret, stripe }) => {
	const { t } = useTranslation('common')
	const [error, setError] = useState<string | null>(null)
	const [formData, setFormData] = useState({
		name: '',
		email: '',
		iban: '',
		termsAccepted: false,
	})

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setError(null)

		if (!formData.termsAccepted) {
			setError('You must agree to the terms and conditions')
			return
		}

		if (!stripe || !clientSecret) {
			setError('Payment system not initialized')
			return
		}

		try {
			// Check if it's a SetupIntent (seti_xxx) or PaymentIntent (pi_xxx)
			const isSetupIntent = clientSecret.startsWith('seti_')

			if (isSetupIntent) {
				// For SetupIntents, use confirmSepaDebitSetup
				const { error: confirmError, setupIntent } =
					await stripe.confirmSepaDebitSetup(clientSecret, {
						payment_method: {
							sepa_debit: {
								iban: formData.iban.replace(/\s/g, ''), // Remove spaces from IBAN
							},
							billing_details: {
								name: formData.name,
								email: formData.email,
							},
						},
					})

				if (confirmError) {
					setError(confirmError.message || 'Setup confirmation failed')
					return
				}

				if (!setupIntent) {
					setError('Setup intent not found')
					return
				}

				const paymentMethodId =
					typeof setupIntent.payment_method === 'string'
						? setupIntent.payment_method
						: setupIntent.payment_method?.id

				if (!paymentMethodId) {
					setError('Payment method not found')
					return
				}

				// Setup succeeded
				await onSubmit(setupIntent.id, paymentMethodId)
			} else {
				// For PaymentIntents, use confirmSepaDebitPayment
				const { error: confirmError, paymentIntent } =
					await stripe.confirmSepaDebitPayment(clientSecret, {
						payment_method: {
							sepa_debit: {
								iban: formData.iban.replace(/\s/g, ''), // Remove spaces from IBAN
							},
							billing_details: {
								name: formData.name,
								email: formData.email,
							},
						},
						return_url: window.location.href,
					})

				if (confirmError) {
					setError(confirmError.message || 'Payment confirmation failed')
					return
				}

				if (!paymentIntent) {
					setError('Payment intent not found')
					return
				}

				const paymentMethodId =
					typeof paymentIntent.payment_method === 'string'
						? paymentIntent.payment_method
						: paymentIntent.payment_method?.id

				if (!paymentMethodId) {
					setError('Payment method not found')
					return
				}

				// Payment succeeded or requires action
				if (
					paymentIntent.status === 'succeeded' ||
					paymentIntent.status === 'processing'
				) {
					await onSubmit(paymentIntent.id, paymentMethodId)
				} else {
					setError(`Payment status: ${paymentIntent.status}`)
				}
			}
		} catch (err: any) {
			setError(err?.message || 'Failed to process payment')
		}
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-6">
			<div className="space-y-4">
				{error && (
					<div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
						<p className="text-sm text-destructive">{error}</p>
					</div>
				)}

				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div className="space-y-2">
						<Label htmlFor="sepa-name">Name</Label>
						<Input
							id="sepa-name"
							type="text"
							value={formData.name}
							onChange={(e) =>
								setFormData({ ...formData, name: e.target.value })
							}
							placeholder="John Doe"
							required
							className="bg-input"
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="sepa-email">Email</Label>
						<Input
							id="sepa-email"
							type="email"
							value={formData.email}
							onChange={(e) =>
								setFormData({ ...formData, email: e.target.value })
							}
							placeholder="jdoe@email.com"
							required
							className="bg-input"
						/>
					</div>
				</div>

				<div className="space-y-2">
					<Label htmlFor="sepa-iban">Bank Account</Label>
					<Input
						id="sepa-iban"
						type="text"
						value={formData.iban}
						onChange={(e) => {
							// Format IBAN with spaces every 4 characters
							const value = e.target.value.replace(/\s/g, '')
							const formatted = value.match(/.{1,4}/g)?.join(' ') || value
							setFormData({ ...formData, iban: formatted })
						}}
						placeholder="ESOO 0000 0000 0000 0000 0000"
						required
						className="bg-input"
						maxLength={34}
					/>
				</div>

				<div className="flex items-start space-x-2">
					<Checkbox
						id="sepa-terms"
						checked={formData.termsAccepted}
						onCheckedChange={(checked) =>
							setFormData({ ...formData, termsAccepted: checked === true })
						}
						required
					/>
					<Label
						htmlFor="sepa-terms"
						className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
					>
						Agree the{' '}
						<a
							href="https://my.brandtrack.ai/terms-and-conditions"
							target="_blank"
							rel="noopener noreferrer"
							className="text-primary underline hover:text-primary/80"
						>
							terms and conditions
						</a>
						*
					</Label>
				</div>
			</div>

			<div className="flex gap-4 pt-6">
				<Button
					type="button"
					variant="outline"
					onClick={onCancel}
					className="rounded-full cursor-pointer"
					disabled={isSubmitting}
				>
					{t('actions.cancel')}
				</Button>
				<Button
					type="submit"
					className="rounded-full cursor-pointer"
					disabled={isSubmitting || !formData.termsAccepted}
				>
					{isSubmitting ? t('actions.saving') : 'Save payment method'}
				</Button>
			</div>
		</form>
	)
}

// Payment form component (uses Stripe hooks, must be inside Elements)
const StripePaymentForm: React.FC<{
	onSubmit: (paymentIntentId: string, paymentMethodId: string) => Promise<void>
	onCancel: () => void
	isSubmitting: boolean
}> = ({ onSubmit, onCancel, isSubmitting }) => {
	const { t } = useTranslation('common')
	const stripe = useStripe()
	const elements = useElements()
	const [isReady, setIsReady] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()

		if (!stripe || !elements) {
			return
		}

		setError(null)

		// Submit the form
		const { error: submitError, paymentIntent } = await stripe.confirmPayment({
			elements,
			confirmParams: {
				// Return URL after payment confirmation
				return_url: window.location.href,
			},
			redirect: 'if_required',
		})

		if (submitError) {
			setError(submitError.message || 'An error occurred')
			return
		}

		if (!paymentIntent) {
			setError('Payment intent not found')
			return
		}

		// Get payment method from elements
		const paymentElement = elements.getElement('payment')
		if (!paymentElement) {
			setError('Payment element not found')
			return
		}

		// Get the payment method from the payment intent
		const paymentMethodId =
			typeof paymentIntent.payment_method === 'string'
				? paymentIntent.payment_method
				: paymentIntent.payment_method?.id

		if (!paymentMethodId) {
			setError('Payment method not found')
			return
		}

		if (paymentIntent.status === 'succeeded') {
			// Payment succeeded, create payment token
			try {
				await onSubmit(paymentIntent.id, paymentMethodId)
			} catch (err: any) {
				setError(err?.message || 'Failed to save payment method')
			}
		} else if (paymentIntent.status === 'requires_action') {
			// Handle 3D Secure or other actions
			const { error: actionError, paymentIntent: updatedPaymentIntent } =
				await stripe.confirmCardPayment(paymentIntent.client_secret as string)

			if (actionError) {
				setError(actionError.message || 'Payment confirmation failed')
				return
			}

			// Retry submission after action
			if (updatedPaymentIntent && updatedPaymentIntent.status === 'succeeded') {
				const finalPaymentMethodId =
					typeof updatedPaymentIntent.payment_method === 'string'
						? updatedPaymentIntent.payment_method
						: updatedPaymentIntent.payment_method?.id

				if (finalPaymentMethodId) {
					try {
						await onSubmit(updatedPaymentIntent.id, finalPaymentMethodId)
					} catch (err: any) {
						setError(err?.message || 'Failed to save payment method')
					}
				}
			}
		} else {
			setError(`Payment status: ${paymentIntent.status}`)
		}
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-6">
			<div className="space-y-4">
				{error && (
					<div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
						<p className="text-sm text-destructive">{error}</p>
					</div>
				)}

				<div className="bg-background border border-border rounded-lg p-4">
					<PaymentElement
						options={{
							layout: 'tabs',
							fields: {
								billingDetails: 'auto',
							},
							terms: {
								card: 'never',
							},
						}}
						onReady={() => {
							setIsReady(true)
						}}
					/>
				</div>
			</div>

			<div className="flex gap-4 pt-6">
				<Button
					type="button"
					variant="outline"
					onClick={() => {
						setIsReady(false)
						onCancel()
					}}
					className="rounded-full cursor-pointer"
					disabled={isSubmitting}
				>
					{t('actions.cancel')}
				</Button>
				<Button
					type="submit"
					className="rounded-full cursor-pointer"
					disabled={!stripe || !isReady || isSubmitting}
				>
					{isSubmitting ? t('actions.saving') : 'Save payment method'}
				</Button>
			</div>
		</form>
	)
}

// Main component that conditionally renders selection or payment form
export const StripeCardForm: React.FC<StripeCardFormProps> = ({
	onSubmit,
	onCancel,
	isSubmitting,
	availablePaymentMethods,
	isLoadingPaymentMethods,
	onSelectPaymentMethod,
	isCreatingIntent,
	hasClientSecret = false,
	selectedPaymentMethodType = null,
	clientSecretValue = null,
}) => {
	// Extract available payment methods to determine if we need selection
	const availableMethods = useMemo(() => {
		if (!availablePaymentMethods) return { card: null, sepa: null }

		const methods =
			availablePaymentMethods?.data || availablePaymentMethods || []
		const methodsArray = Array.isArray(methods) ? methods : []

		const cardMethod = methodsArray.find((method: any) => {
			const methodData = method?.data || method
			return methodData?.payment_type === 'CARD'
		})

		const sepaMethod = methodsArray.find((method: any) => {
			const methodData = method?.data || method
			return methodData?.payment_type === 'SEPA'
		})

		return {
			card: cardMethod
				? {
						id: cardMethod.id || cardMethod.payment_platform_method_id,
						name: cardMethod.name || 'Credit Card',
					}
				: null,
			sepa: sepaMethod
				? {
						id: sepaMethod.id || sepaMethod.payment_platform_method_id,
						name: sepaMethod.name || 'SEPA',
					}
				: null,
		}
	}, [availablePaymentMethods])

	// Show selection form only if both CARD and SEPA are available and no clientSecret yet
	if (!hasClientSecret && availableMethods.card && availableMethods.sepa) {
		return (
			<PaymentMethodSelection
				availablePaymentMethods={availablePaymentMethods}
				isLoadingPaymentMethods={isLoadingPaymentMethods}
				onSelectPaymentMethod={onSelectPaymentMethod}
				isCreatingIntent={isCreatingIntent}
				onCancel={onCancel}
			/>
		)
	}

	// Show loading state while creating intent for card (auto-selected)
	if (
		!hasClientSecret &&
		availableMethods.card &&
		!availableMethods.sepa &&
		isCreatingIntent
	) {
		return (
			<div className="space-y-6 min-h-[550px]">
				<div className="space-y-4">
					<Skeleton className="h-[200px] w-full rounded-lg" />
				</div>
				<div className="flex gap-4 pt-6">
					<Skeleton className="h-10 w-24 rounded-full" />
					<Skeleton className="h-10 w-40 rounded-full" />
				</div>
			</div>
		)
	}

	// Show SEPA form if SEPA is selected (must be inside Elements)
	if (selectedPaymentMethodType === 'SEPA' && hasClientSecret) {
		return (
			<SepaPaymentFormWrapper
				onSubmit={onSubmit}
				onCancel={onCancel}
				isSubmitting={isSubmitting}
				clientSecret={clientSecretValue || ''}
			/>
		)
	}

	// Show card payment form (will be wrapped in Elements by parent)
	if (hasClientSecret) {
		return (
			<StripePaymentForm
				onSubmit={onSubmit}
				onCancel={onCancel}
				isSubmitting={isSubmitting}
			/>
		)
	}

	// Default: show loading or selection
	return (
		<PaymentMethodSelection
			availablePaymentMethods={availablePaymentMethods}
			isLoadingPaymentMethods={isLoadingPaymentMethods}
			onSelectPaymentMethod={onSelectPaymentMethod}
			isCreatingIntent={isCreatingIntent}
			onCancel={onCancel}
		/>
	)
}

// Wrapper for SEPA form that gets stripe instance (needs to be inside Elements)
const SepaPaymentFormWrapper: React.FC<{
	onSubmit: (paymentIntentId: string, paymentMethodId: string) => Promise<void>
	onCancel: () => void
	isSubmitting: boolean
	clientSecret: string | null
}> = ({ onSubmit, onCancel, isSubmitting, clientSecret }) => {
	const stripe = useStripe()

	if (!stripe || !clientSecret) {
		return (
			<div className="space-y-6 min-h-[550px]">
				<div className="space-y-4">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div className="space-y-2">
							<Skeleton className="h-4 w-16" />
							<Skeleton className="h-10 w-full" />
						</div>
						<div className="space-y-2">
							<Skeleton className="h-4 w-16" />
							<Skeleton className="h-10 w-full" />
						</div>
					</div>
					<div className="space-y-2">
						<Skeleton className="h-4 w-24" />
						<Skeleton className="h-10 w-full" />
					</div>
					<div className="flex items-start space-x-2">
						<Skeleton className="h-4 w-4 rounded" />
						<Skeleton className="h-4 w-48" />
					</div>
				</div>
				<div className="flex gap-4 pt-6">
					<Skeleton className="h-10 w-24 rounded-full" />
					<Skeleton className="h-10 w-40 rounded-full" />
				</div>
			</div>
		)
	}

	return (
		<SepaPaymentForm
			onSubmit={onSubmit}
			onCancel={onCancel}
			isSubmitting={isSubmitting}
			clientSecret={clientSecret}
			stripe={stripe}
		/>
	)
}
